import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation, Trans } from "react-i18next";
import { Event } from "@uno/protocol";
import { AppContext } from "context";
import useLocalStorage from "hook/useLocalStorage";
import useStorePlayer from "store/useStorePlayer";
import LongPressButton from "component/Button/LongPressButton";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import { Action } from "../types";

type Props = {
    dispatch: React.Dispatch<Action>;
    theme: "light" | "dark";
    onSuccess: () => void;
};

const QrBackupStep = ({ dispatch, theme, onSuccess }: Props) => {
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const username = useStorePlayer((state) => state.player?.username ?? "");
    const [token] = useLocalStorage("LASTDOTCARDS.USER_IDENTITY");
    const [revealed, setRevealed] = React.useState(false);
    const [acknowledged, setAcknowledged] = React.useState(false);

    const handleDownload = () => {
        const source = document.querySelector("#signup-qr canvas") as HTMLCanvasElement;

        if(!source) {
            return;
        }

        const padding = 40;
        const size = 512;
        const output = document.createElement("canvas");
        output.width = size + padding * 2;
        output.height = size + padding * 2;

        const ctx = output.getContext("2d");

        if(!ctx) {
            return;
        }

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, output.width, output.height);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(source, padding, padding, size, size);

        const link = document.createElement("a");
        link.href = output.toDataURL("image/png");
        link.download = `LAST_${username || "BACKUP"}.png`;
        link.click();
    };

    const handleConfirm = () => {
        socket?.emit(Event.PlayerConfirmBackup);
        dispatch({ type: "QR_CONFIRMED" });
        onSuccess();
    };

    const isDark = theme === "dark";

    return (
        <>
            <div className="flex flex-col items-center gap-1.5">
                <p className="font-changa-one text-base text-center">
                    {t("signup.qr_title")}
                </p>
                <p className="text-sm text-center opacity-60 leading-relaxed">
                    {t("signup.qr_description")}
                    <br />
                    <Trans i18nKey="signup.qr_warning" components={{ 1: <strong className="font-bold" /> }} />
                </p>
            </div>

            <div id="signup-qr" className="relative bg-white rounded-xl p-5 flex justify-center overflow-hidden mt-1">
                <div className={`transition-[filter] duration-300 ${revealed ? "blur-0" : "blur-md"}`}>
                    <QRCodeCanvas value={token || ""} size={180} level="M" />
                </div>
                {!revealed && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <ButtonHapticWrapper>
                            <button
                                onClick={() => setRevealed(true)}
                                className="px-4 py-2 rounded-full font-changa-one text-xs tracking-wide text-white cursor-pointer bg-black/40 backdrop-blur-md border border-white/30 shadow-lg"
                            >
                                {t("signup.qr_reveal")}
                            </button>
                        </ButtonHapticWrapper>
                    </div>
                )}
            </div>

            {revealed && (
                <button
                    onClick={handleDownload}
                    className="text-xs underline underline-offset-2 opacity-50 hover:opacity-80 cursor-pointer transition-opacity"
                >
                    {t("signup.qr_download")}
                </button>
            )}

            {revealed && (
                <label className="flex items-start gap-2.5 w-full cursor-pointer select-none mt-2">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(event) => setAcknowledged(event.target.checked)}
                        className="mt-0.5 w-4 h-4 shrink-0 accent-blue-500 rounded"
                    />
                    <span className="text-[11px] opacity-60 leading-relaxed">
                        {t("signup.qr_acknowledge")}
                    </span>
                </label>
            )}

            <div className="w-full mt-2">
                <LongPressButton
                    onConfirm={acknowledged ? handleConfirm : () => undefined}
                    className={`w-full h-10 rounded-xl font-changa-one text-sm tracking-wide uppercase transition-colors ${
                        acknowledged
                            ? isDark
                                ? "bg-white text-gray-900 cursor-pointer"
                                : "bg-blue-900 text-white cursor-pointer"
                            : "opacity-30 cursor-not-allowed"
                    }`}
                >
                    {t("signup.qr_confirm")}
                </LongPressButton>
            </div>
        </>
    );
};

export default QrBackupStep;
